import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  ExternalLink, 
  Share2, 
  Copy, 
  Check,
  Facebook,
  Twitter,
  Mail,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

export default function GameDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Handle back navigation - go back to the game card
  const handleBackClick = (e) => {
    e.preventDefault();
    // Navigate to home with the game ID as a hash to scroll to
    navigate(`/#game-${id}`);
  };

  // Track analytics
  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${API}/analytics`, { game_id: id, event_type: eventType });
    } catch (error) {
      // Silent fail for analytics
    }
  };

  useEffect(() => {
    const fetchGame = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/games/${id}`);
        setGame(response.data);
      } catch (error) {
        console.error("Error fetching game:", error);
        setGame(null);
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [id]);

  const shareUrl = window.location.href;
  
  // Get video IDs from youtube_urls array (or fall back to old youtube_url field)
  const videoIds = game 
    ? (game.youtube_urls && game.youtube_urls.length > 0 
        ? game.youtube_urls.map(url => getYouTubeVideoId(url)).filter(Boolean)
        : game.youtube_url ? [getYouTubeVideoId(game.youtube_url)].filter(Boolean) : [])
    : [];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      trackEvent('share');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleShare = (platform) => {
    trackEvent('share');
  };

  const handlePurchaseClick = () => {
    trackEvent('click_purchase');
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(game?.name || "")}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`Check out ${game?.name}: ${shareUrl}`)}`,
    email: `mailto:?subject=${encodeURIComponent(game?.name || "")}&body=${encodeURIComponent(`Check out this game: ${shareUrl}`)}`
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="skeleton h-8 w-32 rounded mb-8" />
          <div className="skeleton h-[400px] w-full rounded-2xl mb-8" />
          <div className="skeleton h-12 w-3/4 rounded mb-4" />
          <div className="skeleton h-24 w-full rounded" />
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif font-bold text-2xl text-slate-900 mb-4">
            Game not found
          </h2>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#7C3AED] font-sans font-medium hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Games
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-sans font-medium mb-8 group"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Games
        </button>

        {/* Game Content */}
        <div className="animate-fade-in-up">
          {/* Game Image */}
          <div className="relative rounded-3xl overflow-hidden mb-8 shadow-2xl">
            <img
              src={game.image_url}
              alt={game.name}
              className="w-full h-[300px] md:h-[450px] object-cover"
              data-testid="game-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>

          {/* Title and Actions */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
            <div>
              <h1 
                className="font-serif font-bold text-3xl md:text-5xl text-slate-900 mb-4"
                data-testid="game-title"
              >
                {game.name}
              </h1>
              <p 
                className="font-sans text-slate-600 text-lg leading-relaxed max-w-2xl"
                data-testid="game-description"
              >
                {game.description}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 shrink-0">
              {/* Share Button */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full px-6 py-6 border-2 border-stone-200 hover:border-slate-900 font-sans font-bold"
                    data-testid="share-btn"
                  >
                    <Share2 className="w-5 h-5 mr-2" />
                    Share
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem 
                    onClick={handleCopyLink}
                    className="cursor-pointer"
                    data-testid="share-copy"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 mr-2 text-[#7C3AED]" />
                    ) : (
                      <Copy className="w-4 h-4 mr-2" />
                    )}
                    Copy Link
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a 
                      href={shareLinks.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cursor-pointer"
                      onClick={() => handleShare('facebook')}
                      data-testid="share-facebook"
                    >
                      <Facebook className="w-4 h-4 mr-2" />
                      Facebook
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a 
                      href={shareLinks.twitter} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cursor-pointer"
                      onClick={() => handleShare('twitter')}
                      data-testid="share-twitter"
                    >
                      <Twitter className="w-4 h-4 mr-2" />
                      Twitter / X
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a 
                      href={shareLinks.whatsapp} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cursor-pointer"
                      onClick={() => handleShare('whatsapp')}
                      data-testid="share-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a 
                      href={shareLinks.email}
                      className="cursor-pointer"
                      onClick={() => handleShare('email')}
                      data-testid="share-email"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Buy/Project Button */}
              <a
                href={game.purchase_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handlePurchaseClick}
                className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-full font-sans font-bold hover:scale-105 active:scale-95 transition-all shadow-lg bg-[#7C3AED] hover:bg-[#6D28D9] shadow-[#7C3AED]/20"
                data-testid="purchase-btn"
              >
                <ExternalLink className="w-5 h-5" />
                {game.category === "crowdfunding" ? "See Project Page" : "Buy Game"}
              </a>
            </div>
          </div>

          {/* YouTube Videos */}
          {videoIds.length > 0 && (
            <div className="mt-12">
              <h2 className="font-serif font-bold text-2xl text-slate-900 mb-6">
                {videoIds.length === 1 ? "Watch the Video" : "Watch the Videos"}
              </h2>
              <div className="space-y-6">
                {videoIds.map((videoId, index) => (
                  <div 
                    key={videoId}
                    className="video-container rounded-2xl overflow-hidden shadow-xl"
                    data-testid={`youtube-video-${index}`}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={`${game.name} video ${index + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
